import React, {useContext, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {RootState, AppDispatch} from '../../store/store';
import {TitleContext} from '../../components/TitleContext';
import Skeleton from 'react-loading-skeleton';
import {FaUser} from 'react-icons/fa';
import DotsMenu from '../../components/DotsMenu';
import ClientSideBar from '../../components/ClientSideBar';
import {ClientVisitThunks} from '../../features/client/clientVisit/clientVisitThunks';
import VisitDocumentsTable from '../../features/visit/documents/VisitDocumentsTable';
import VisitDocumentForm from '../../features/visit/documents/VisitDocumentForm';
import Drawer from '../../components/Drawer';

const VisitField: React.FC<{
  label: string;
  value?: React.ReactNode;
  loading: boolean;
  width?: number | string;
}> = ({label, value, loading, width = 200}) => (
  <div className="flex flex-col" style={{width}}>
    <div className="text-slate-400 text-xs font-light font-['Inter'] mb-1">
      {label}
    </div>
    <div className="text-slate-800 text-sm font-medium font-['Inter']">
      {loading ? <Skeleton width={width} height={20} /> : value || '-'}
    </div>
  </div>
);

const VisitPage: React.FC = () => {
  const {id} = useParams<{id: string}>();
  const visit = useSelector((state: RootState) => state.clientVisit.clientVisit);
  const loading = useSelector(
    (state: RootState) => state.clientVisit.status === 'loading',
  );
  const {setTitle} = useContext(TitleContext);
  const dispatch = useDispatch<AppDispatch>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);

  // Load visit data when component mounts or id changes
  useEffect(() => {
    if (id) {
      dispatch(ClientVisitThunks.show(id));
    }
  }, [id, dispatch]);


  useEffect(() => {
    if (loading && !visit) {
      setTitle(
        <div>
          <div className="text-slate-600 text-lg font-medium font-['Inter']  ">
            <Skeleton width={200} height={24} />
          </div>
        </div>,
      );
    } else if (visit && visit.id) {
      setTitle(
        <div>
          <div className="text-slate-600 text-lg font-medium font-['Inter']  ">
            Visit #{visit.id}
          </div>
        </div>,
      );
    } else {
      setTitle(
        <div>
          <div className="text-slate-600 text-lg font-medium font-['Inter']  ">
            Visit Details
          </div>
        </div>,
      );
    }
  }, [setTitle, visit, loading]);

  if (loading && !visit) {
    return (
      <div className="flex flex-row h-full">
        <div className="flex-1 flex flex-col">
          <div className="bg-white border border-slate-200 dark:bg-neutral-800 p-6 rounded-lg flex flex-row items-center gap-8 mb-6 relative">
            <div className="absolute top-6 right-6 z-10">
              <Skeleton width={40} height={40} />
            </div>
            <div className="flex-shrink-0 w-32 h-32 mx-14 bg-slate-100 rounded-full flex justify-center items-center overflow-hidden">
              <Skeleton circle width={128} height={128} />
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-1">
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
              <Skeleton width={200} height={40} />
            </div>
          </div>
          
          {/* Skeleton for tabs content */}
          <div className="p-5 space-y-4 flex flex-col bg-white border border-gray-200 rounded-xl dark:bg-neutral-800 dark:border-neutral-700">
            <div className="text-sm font-medium text-center text-slate-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700 overflow-x-auto overflow-y-clip">
              <ul className="flex flex-nowrap -mb-px min-w-max">
                <Skeleton width={80} height={40} className="me-2" />
                <Skeleton width={70} height={40} className="me-2" />
              </ul>
            </div>
            <div className="space-y-4">
              <Skeleton height={200} />
              <Skeleton height={100} />
            </div>
          </div>
        </div>
        <div className="w-80">
          <Skeleton height={400} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row h-full ">
      <div className="flex-1 flex flex-col">
        <div className="bg-white border border-slate-200 dark:bg-neutral-800 p-6 rounded-lg  flex flex-row items-center gap-8 mb-6 relative">
          <div className="absolute top-6 right-6 z-10">
            <DotsMenu
              options={[
                // { label: 'Edit Visit', onClick: () => {} },
                // { label: 'Delete Visit', onClick: () => {} },
              ]}
            />
          </div>
          <div className="flex-shrink-0 w-32 h-32 mx-14 bg-slate-100 rounded-full flex justify-center items-center overflow-hidden">
            {visit?.client?.pictureUrl ? (
              <img
                src={visit.client.pictureUrl}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <FaUser className="text-gray-400 size-16" />
            )}
          </div>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-1">
            <VisitField
              label="Visit ID"
              value={visit?.id}
              loading={loading}
              width={200}
            />
            <VisitField
              label="Visit Date"
              value={visit?.visitDate}
              loading={loading}
              width={200}
            />
            <VisitField
              label="Status"
              value={visit?.status}
              loading={loading}
              width={200}
            />
            <VisitField
              label="Program"
              value={visit?.program?.name}
              loading={loading}
              width={200}
            />
            <VisitField
              label="Client"
              value={
                visit?.client
                  ? `${visit.client.firstName} ${visit.client.lastName}`
                  : '-'
              }
              loading={loading}
              width={200}
            />
            <VisitField
              label="Risk Level"
              value={visit?.riskLevel}
              loading={loading}
              width={200}
            />
            <VisitField
              label="Notes"
              value={visit?.notes}
              loading={loading}
              width={200}
            />
            <VisitField
              label="Safety Plan As Of"
              value={visit?.safetyPlanAsOf}
              loading={loading}
              width={200}
            />
          </div>
        </div>
        
        <div>
          <VisitDocumentsTable openDrawer={openDrawer} visitId={id}/>
          <Drawer isOpen={isDrawerOpen} onClose={closeDrawer}>
            <VisitDocumentForm closeDrawer={closeDrawer} visitId={id || ''} isDrawerOpen={isDrawerOpen} />
          </Drawer>
        </div>
      </div>
      <ClientSideBar/>
    </div>
  );
};

export default VisitPage;
